// src/components/SensorPage/Dashboard.jsx - Design Ultra Premium
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiActivity,
  FiTrendingUp,
  FiThermometer,
  FiDroplet,
  FiWind,
  FiSun,
  FiClock,
  FiZap,
  FiAlertCircle,
  FiCheckCircle,
  FiRefreshCw,
  FiMaximize2,
  FiMinimize2,
  FiDownload,
  FiFilter,
  FiCalendar,
  FiBell,
  FiEye,
  FiEyeOff,
  FiGrid,
  FiList,
  FiBarChart2,
  FiPieChart,
  FiMap,
  FiLayers,
  FiVolume2
} from 'react-icons/fi';
import { 
  LineChart, 
  Line, 
  AreaChart, 
  Area,
  BarChart,
  Bar,
  ComposedChart,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  RadialBarChart,
  RadialBar,
  PieChart,
  Pie,
  Cell,
  Scatter,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar
} from 'recharts';
import CountUp from 'react-countup';
import './Dashboard.css';

const Dashboard = ({ data, activeSubsection, onTimeRangeChange, timeRange: parentTimeRange }) => {
  // Usar timeRange do componente pai ou padrão '24h'
  const timeRange = parentTimeRange || '24h';
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [viewMode, setViewMode] = useState('grid'); // grid ou list
  const [selectedMetrics, setSelectedMetrics] = useState(['all']);
  const [fullscreenMetric, setFullscreenMetric] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  
  const { current, historical, strategic, availableReadings } = data || {};

  // Auto-refresh
  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(() => {
        setRefreshKey(prev => prev + 1);
      }, 30000); // 30 segundos
      return () => clearInterval(interval);
    }
  }, [autoRefresh]);

  // Função para formatar tempo baseado no timeRange
  const formatTimeForChart = (timestamp, range) => {
    const date = new Date(timestamp);
    
    switch (range) {
      case '1h':
        return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
      case '24h':
        return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
      case '7d':
        return date.toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: '2-digit' });
      case '30d':
        return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
      case '6m':
      case '1y':
      case 'all':
        return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' });
      default:
        return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    }
  };

  // Dados processados para gráficos (tratar valores null e incluir todas as leituras disponíveis)
  const processedData = useMemo(() => {
    if (!historical || historical.length === 0) return [];
    
    return historical.map((item) => ({
      timestamp: item.timestamp,
      time: formatTimeForChart(item.timestamp, timeRange),
      // Incluir apenas leituras que o sensor possui
      temperatura: (availableReadings?.temperature && item.temperature !== null && item.temperature !== undefined) ? item.temperature : null,
      umidade: (availableReadings?.humidity && item.humidity !== null && item.humidity !== undefined) ? item.humidity : null,
      co2: (availableReadings?.co2 && item.co2 !== null && item.co2 !== undefined) ? item.co2 : null,
      pm25: (availableReadings?.pm25 && item.pm25 !== null && item.pm25 !== undefined) ? item.pm25 : null,
      pm10: (availableReadings?.pm10 && item.pm10 !== null && item.pm10 !== undefined) ? item.pm10 : null,
      pm1: (availableReadings?.pm1 && item.pm1 !== null && item.pm1 !== undefined) ? item.pm1 : null,
      voc: (availableReadings?.voc && item.voc !== null && item.voc !== undefined) ? item.voc : null,
      dewpoint: (availableReadings?.dewpoint && item.dewpoint !== null && item.dewpoint !== undefined) ? item.dewpoint : null,
      wetbulb: (availableReadings?.wetbulb && item.wetbulb !== null && item.wetbulb !== undefined) ? item.wetbulb : null,
      enthalpy: (availableReadings?.enthalpy && item.enthalpy !== null && item.enthalpy !== undefined) ? item.enthalpy : null,
      absoluteHumidity: (availableReadings?.absoluteHumidity && item.absoluteHumidity !== null && item.absoluteHumidity !== undefined) ? item.absoluteHumidity : null,
      specificHumidity: (availableReadings?.specificHumidity && item.specificHumidity !== null && item.specificHumidity !== undefined) ? item.specificHumidity : null,
      luminosidade: (availableReadings?.luminosity && item.luminosity !== null && item.luminosity !== undefined) ? item.luminosity : null,
      ruido: (availableReadings?.noise && item.noise !== null && item.noise !== undefined) ? item.noise : null
    }));
  }, [historical, timeRange, availableReadings]);

  // Função helper para obter unidade de medida baseada no tipo de métrica
  const getMetricUnit = (metricId) => {
    const unitMap = {
      'temperatura': '°C',
      'temp': '°C',
      'temperature': '°C',
      'umidade': '%',
      'humidity': '%',
      'co2': 'ppm',
      'pm25': 'µg/m³',
      'pm10': 'µg/m³',
      'pm1': 'µg/m³',
      'voc': 'ppb',
      'dewpoint': '°C',
      'wetbulb': '°C',
      'enthalpy': 'kJ/kg',
      'absoluteHumidity': 'g/m³',
      'specificHumidity': 'g/kg',
      'luminosity': 'lux',
      'luminosidade': 'lux',
      'noise': 'dB',
      'ruido': 'dB'
    };
    return unitMap[metricId] || '';
  };

  // Componente para Dados Instantâneos
  const InstantDataView = () => {
    // Função auxiliar para verificar se valor está disponível
    const hasValue = (value) => value !== null && value !== undefined && !isNaN(value);
    

      // Componente wrapper seguro para CountUp
  const SafeCountUp = ({ value, unit = '', decimals = 2, className = '' }) => {
    const [isMounted, setIsMounted] = useState(false);
    const containerRef = useRef(null);
    
    useEffect(() => {
      // Garantir que o componente está montado e o DOM existe
      setIsMounted(true);
    }, []);
    
    if (!hasValue(value)) {
      return (
        <div className="value-container">
          <span className="no-data-value">N/A</span>
          <div className="no-data-tooltip">
            <FiAlertCircle size={14} />
            <span>Leitura não encontrada no banco de dados</span>
          </div>
        </div>
      );
    }
    
    // Garantir que value é um número válido antes de passar para CountUp
    const safeValue = typeof value === 'number' && !isNaN(value) && isFinite(value) && value >= 0 ? value : 0;
    
    if (!isMounted) {
      return (
        <span className={className || 'metric-main-value'}>
          {safeValue.toFixed(decimals)}
          <span className="metric-unit">{unit}</span>
        </span>
      );
    }
    
    return (
      <div ref={containerRef}>
        <CountUp
          start={0}
          end={safeValue}
          duration={1.5}
          decimals={decimals}
          className={className || 'metric-main-value'}
        />
        <span className="metric-unit">{unit}</span>
      </div>
    );
  };
  
  // Renderizar valor ou N/A (mantido para compatibilidade)
  const renderValue = (value, unit = '', decimals = 1) => {
    return <SafeCountUp value={value} unit={unit} decimals={decimals} />;
  };
  
    console.log(current);
    
    // Mapear dados históricos para sparklines
    const getSparklineData = (metricId) => {
      const fieldMap = {
        'temp': 'temperatura',
        'humidity': 'umidade',
        'co2': 'co2',
        'pm25': 'pm25',
        'pm10': 'pm10',
        'pm1': 'pm1',
        'voc': 'voc',
        'dewpoint': 'dewpoint',
        'wetbulb': 'wetbulb',
        'enthalpy': 'enthalpy',
        'absoluteHumidity': 'absoluteHumidity',
        'specificHumidity': 'specificHumidity',
        'luminosity': 'luminosidade',
        'noise': 'ruido'
      };
      const field = fieldMap[metricId];
      if (!field) return [];
      
      return processedData
        .map(d => d[field])
        .filter(v => v !== null && v !== undefined && !isNaN(v))
        .slice(-20); // Últimos 20 pontos
    };
    
    // Todas as métricas disponíveis baseadas em availableReadings
    const allMetricsConfig = [
      { id: 'temp', name: 'Temperatura', field: 'temperature', unit: '°C', icon: FiThermometer, color: '#ef4444', sparklineId: 'temp', min: 0, max: 50, target: 22, reading: 'temperature' },
      { id: 'humidity', name: 'Umidade', field: 'humidity', unit: '%', icon: FiDroplet, color: '#3b82f6', sparklineId: 'humidity', min: 0, max: 100, target: 50, reading: 'humidity' },
      { id: 'co2', name: 'CO₂', field: 'co2', unit: 'ppm', icon: FiWind, color: '#10b981', sparklineId: 'co2', min: 0, max: 2000, target: 400, reading: 'co2' },
      { id: 'pm25', name: 'PM2.5', field: 'pm25', unit: 'µg/m³', icon: FiSun, color: '#f59e0b', sparklineId: 'pm25', min: 0, max: 100, target: 12, reading: 'pm25' },
      { id: 'pm10', name: 'PM10', field: 'pm10', unit: 'µg/m³', icon: FiSun, color: '#f97316', sparklineId: 'pm10', min: 0, max: 100, target: 20, reading: 'pm10' },
      { id: 'pm1', name: 'PM1', field: 'pm1', unit: 'µg/m³', icon: FiSun, color: '#fb923c', sparklineId: 'pm1', min: 0, max: 100, target: 10, reading: 'pm1' },
      { id: 'voc', name: 'VOC', field: 'voc', unit: 'ppb', icon: FiLayers, color: '#8b5cf6', sparklineId: 'voc', min: 0, max: 1000, target: 250, reading: 'voc' },
      { id: 'dewpoint', name: 'Ponto de Orvalho', field: 'dewpoint', unit: '°C', icon: FiThermometer, color: '#06b6d4', sparklineId: 'dewpoint', min: -10, max: 30, target: 10, reading: 'dewpoint' },
      { id: 'wetbulb', name: 'Bulbo Úmido', field: 'wetbulb', unit: '°C', icon: FiThermometer, color: '#14b8a6', sparklineId: 'wetbulb', min: 0, max: 50, target: 20, reading: 'wetbulb' },
      { id: 'enthalpy', name: 'Entalpia', field: 'enthalpy', unit: 'kJ/kg', icon: FiZap, color: '#ec4899', sparklineId: 'enthalpy', min: 0, max: 100, target: 50, reading: 'enthalpy' },
      { id: 'absoluteHumidity', name: 'Umidade Absoluta', field: 'absoluteHumidity', unit: 'g/m³', icon: FiDroplet, color: '#6366f1', sparklineId: 'absoluteHumidity', min: 0, max: 30, target: 10, reading: 'absoluteHumidity' },
      { id: 'specificHumidity', name: 'Umidade Específica', field: 'specificHumidity', unit: 'g/kg', icon: FiDroplet, color: '#8b5cf6', sparklineId: 'specificHumidity', min: 0, max: 20, target: 8, reading: 'specificHumidity' },
      { id: 'luminosity', name: 'Luminosidade', field: 'luminosity', unit: 'lux', icon: FiSun, color: '#fbbf24', sparklineId: 'luminosity', min: 0, max: 1000, target: 500, reading: 'luminosity' },
      { id: 'noise', name: 'Ruído', field: 'noise', unit: 'dB', icon: FiVolume2, color: '#64748b', sparklineId: 'noise', min: 0, max: 100, target: 50, reading: 'noise' }
    ];
    
    // Filtrar apenas métricas que o sensor possui
    const realtimeMetrics = allMetricsConfig
      .filter(config => {
        // Se não houver availableReadings, mostrar todas (compatibilidade)
        if (!availableReadings || Object.keys(availableReadings).length === 0) {
          return ['temp', 'humidity', 'co2', 'pm25', 'voc'].includes(config.id);
        }
        return availableReadings[config.reading] === true;
      })
      .map(config => ({
        id: config.id,
        name: config.name,
        value: current[config.field],
        unit: config.unit,
        icon: config.icon,
        color: config.color,
        available: hasValue(current[config.field]),
        sparkline: getSparklineData(config.sparklineId),
        min: config.min,
        max: config.max,
        target: config.target,
        trend: 0,
        status: 'normal'
      }));

    // Componente de Card de Métrica
    const MetricCard = ({ metric, index }) => {
      const IconComponent = metric.icon;
      const hasValue = metric.value !== null && metric.value !== undefined && !isNaN(metric.value);
      const range = (metric.max - metric.min) || 1; // Evitar divisão por zero
      const percentage = hasValue && range > 0 ? ((metric.value - metric.min) / range) * 100 : 0;
      
      return (
        <motion.div
          className={`metric-card-realtime ${metric.status}`}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.05 }}
          whileHover={{ y: -5, scale: 1.02 }}
          onClick={() => setFullscreenMetric(metric)}
        >
          <div className="metric-card-header">
            <div className="metric-icon-wrapper" style={{ background: `${metric.color}15` }}>
              <IconComponent size={24} color={metric.color} />
            </div>
            <div className="metric-actions">
              <button className="action-btn">
                <FiMaximize2 size={14} />
              </button>
            </div>
          </div>

          <div className="metric-value-section">
            <div className="value-display">
              {hasValue && 
               metric.value !== null && 
               metric.value !== undefined && 
               typeof metric.value === 'number' &&
               !isNaN(metric.value) && 
               isFinite(metric.value) ? (
                <SafeCountUp
                  value={metric.value}
                  unit={metric.unit}
                  decimals={2}
                  className="metric-main-value"
                />
              ) : (
                <div className="metric-main-value" style={{ color: '#fbbf24' }}>
                  N/A
                  <span 
                    className="tooltip-trigger" 
                    title="Leitura não encontrada no banco de dados"
                    style={{ 
                      marginLeft: '8px', 
                      fontSize: '14px',
                      cursor: 'help'
                    }}
                  >
                    ⚠️
                  </span>
                </div>
              )}
            </div>
            {hasValue && (
              <div className={`trend-indicator ${metric.trend > 0 ? 'up' : 'down'}`}>
                {metric.trend > 0 ? <FiTrendingUp /> : <FiTrendingUp style={{ transform: 'rotate(180deg)' }} />}
                <span>{Math.abs(metric.trend)}%</span>
              </div>
            )}
          </div>

          <div className="metric-name">{metric.name}</div>

          {/* Mini Sparkline */}
          {metric.sparkline && metric.sparkline.length > 0 ? (
            <div className="sparkline-container">
              <ResponsiveContainer width="100%" height={40}>
                <LineChart data={metric.sparkline.map((v, i) => ({ value: v, index: i }))}>
                  <Line 
                    type="monotone" 
                    dataKey="value" 
                    stroke={metric.color}
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="sparkline-container" style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              color: '#9ca3af',
              fontSize: '12px'
            }}>
              Sem dados históricos
            </div>
          )}

          {/* Progress Bar */}
          {hasValue && (
            <div className="metric-progress">
              <div className="progress-track">
                <motion.div 
                  className="progress-fill"
                  style={{ background: metric.color }}
                  initial={{ width: 0 }}
                  animate={{ width: `${percentage}%` }}
                  transition={{ duration: 1, delay: index * 0.1 }}
                />
                <div 
                  className="target-marker"
                  style={{ left: `${range > 0 ? ((metric.target - metric.min) / range) * 100 : 0}%` }}
                >
                  <div className="marker-line"></div>
                  <div className="marker-tooltip">Target: {metric.target}</div>
                </div>
              </div>
              <div className="progress-labels">
                <span>{metric.min}</span>
                <span>{metric.max}</span>
              </div>
            </div>
          )}

          {/* Status Badge */}
          <div className={`status-badge-realtime ${metric.status}`}>
            {metric.status === 'optimal' && <FiCheckCircle size={14} />}
            {metric.status === 'warning' && <FiAlertCircle size={14} />}
            {metric.status === 'critical' && <FiAlertCircle size={14} />}
            {metric.status === 'no-data' && <FiAlertCircle size={14} />}
            <span>
              {metric.status === 'optimal' && 'Ótimo'}
              {metric.status === 'warning' && 'Atenção'}
              {metric.status === 'critical' && 'Crítico'}
              {metric.status === 'no-data' && 'Sem dados'}
            </span>
          </div>
        </motion.div>
      );
    };

    // Radar Chart para visão geral (tratar valores null)
    const radarData = [
      {
        metric: 'Temperatura',
        atual: hasValue(current.temperature) ? (current.temperature / 30) * 100 : 0,
        ideal: 75
      },
      {
        metric: 'Umidade',
        atual: hasValue(current.humidity) ? current.humidity : 0,
        ideal: 50
      },
      {
        metric: 'CO₂',
        atual: hasValue(current.co2) ? (600 - Math.min(current.co2, 600)) / 6 : 0,
        ideal: 100
      },
      {
        metric: 'PM2.5',
        atual: hasValue(current.pm25) ? (35 - Math.min(current.pm25, 35)) / 0.35 : 0,
        ideal: 90
      },
      {
        metric: 'VOC',
        atual: hasValue(current.voc) ? (250 - Math.min(current.voc, 250)) / 2.5 : 0,
        ideal: 85
      }
    ];

    return (
      <div className="instant-data-container">
        {/* Header Section */}
        <div className="dashboard-header-premium">
          <div className="header-gradient-bg"></div>
          <div className="header-content-dash">
            <div className="header-left-dash">
              <motion.div 
                className="header-icon-dash"
                animate={{ rotate: 360 }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              >
                <FiActivity size={36} />
              </motion.div>
              <div>
                <h1 className="header-title-dash">Dados Instantâneos</h1>
                <p className="header-subtitle-dash">Monitoramento em tempo real do ambiente</p>
              </div>
            </div>
            <div className="header-controls">
              <button 
                className={`control-btn ${autoRefresh ? 'active' : ''}`}
                onClick={() => setAutoRefresh(!autoRefresh)}
              >
                <FiRefreshCw size={18} />
                <span>Auto-refresh</span>
              </button>
              <button 
                className={`control-btn ${viewMode === 'grid' ? 'active' : ''}`}
                onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
              >
                {viewMode === 'grid' ? <FiGrid size={18} /> : <FiList size={18} />}
                <span>{viewMode === 'grid' ? 'Grid' : 'Lista'}</span>
              </button>
              <button className="control-btn">
                <FiDownload size={18} />
                <span>Exportar</span>
              </button>
            </div>
          </div>
        </div>

        {/* Live Status Bar */}
        <div className="live-status-bar">
          <div className="status-item">
            <div className="status-dot live"></div>
            <span>Ao Vivo</span>
          </div>
          <div className="status-item">
            <FiClock size={14} />
            <span>Última atualização: {new Date().toLocaleTimeString('pt-BR')}</span>
          </div>
          <div className="status-item">
            <FiBell size={14} />
            <span>3 alertas ativos</span>
          </div>
        </div>

        {/* Metrics Grid */}
        <div className={`metrics-grid-realtime ${viewMode}`}>
          {realtimeMetrics.map((metric, index) => (
            <MetricCard key={metric.id} metric={metric} index={index} />
          ))}
        </div>

        {/* Overview Radar Chart */}
        <div className="overview-section">
          <div className="overview-card">
            <h3 className="section-title-dash">Visão Geral do Ambiente</h3>
            <ResponsiveContainer width="100%" height={400}>
              <RadarChart data={radarData}>
                <PolarGrid 
                  stroke="#e5e7eb"
                  strokeDasharray="3 3"
                />
                <PolarAngleAxis 
                  dataKey="metric"
                  tick={{ fill: '#6b7280', fontSize: 12 }}
                />
                <PolarRadiusAxis
                  angle={90}
                  domain={[0, 100]}
                  tick={{ fill: '#9ca3af', fontSize: 10 }}
                />
                <Radar
                  name="Atual"
                  dataKey="atual"
                  stroke="#3b82f6"
                  fill="#3b82f6"
                  fillOpacity={0.3}
                  strokeWidth={2}
                />
                <Radar
                  name="Ideal"
                  dataKey="ideal"
                  stroke="#10b981"
                  fill="#10b981"
                  fillOpacity={0.2}
                  strokeWidth={2}
                  strokeDasharray="5 5"
                />
                <Tooltip
                  contentStyle={{
                    background: 'rgba(255, 255, 255, 0.95)',
                    border: 'none',
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                  }}
                />
                <Legend />
              </RadarChart>
            </ResponsiveContainer>
          </div>

          {/* Quick Stats */}
          <div className="quick-stats">
            <motion.div 
              className="stat-card"
              whileHover={{ scale: 1.05 }}
            >
              <div className="stat-icon optimal">
                <FiCheckCircle size={24} />
              </div>
              <div className="stat-info">
                <div className="stat-value">4</div>
                <div className="stat-label">Métricas Ótimas</div>
              </div>
            </motion.div>

            <motion.div 
              className="stat-card"
              whileHover={{ scale: 1.05 }}
            >
              <div className="stat-icon warning">
                <FiAlertCircle size={24} />
              </div>
              <div className="stat-info">
                <div className="stat-value">2</div>
                <div className="stat-label">Requerem Atenção</div>
              </div>
            </motion.div>

            <motion.div 
              className="stat-card"
              whileHover={{ scale: 1.05 }}
            >
              <div className="stat-icon info">
                <FiActivity size={24} />
              </div>
              <div className="stat-info">
                <div className="stat-value">92%</div>
                <div className="stat-label">Saúde Geral</div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    );
  };

  // Componente para Histórico de Leituras
  const HistoricalView = () => {
    // Usar timeRange diretamente do componente pai, sem estado local
    const selectedPeriod = timeRange;
    const [chartType, setChartType] = useState('area');
    
    // Carregar métricas selecionadas do localStorage
    const getStoredMetrics = () => {
      try {
        const stored = localStorage.getItem(`selectedMetrics_${data?.sensorId || 'default'}`);
        if (stored) {
          const parsed = JSON.parse(stored);
          return Array.isArray(parsed) ? parsed : [];
        }
      } catch (e) {
        console.warn('Erro ao carregar métricas do localStorage:', e);
      }
      return [];
    };
    
    const [localSelectedMetrics, setLocalSelectedMetrics] = useState(() => getStoredMetrics());
    const initializedRef = useRef(false);
    
    // Salvar métricas selecionadas no localStorage
    const saveMetricsToStorage = useCallback((metrics) => {
      try {
        localStorage.setItem(`selectedMetrics_${data?.sensorId || 'default'}`, JSON.stringify(metrics));
      } catch (e) {
        console.warn('Erro ao salvar métricas no localStorage:', e);
      }
    }, [data?.sensorId]);

    const periods = [
      { id: '1h', label: '1 Hora' },
      { id: '24h', label: '24 Horas' },
      { id: '7d', label: '7 Dias' },
      { id: '30d', label: '30 Dias' },
      { id: '6m', label: '6 Meses' },
      { id: '1y', label: '1 Ano' },
      { id: 'all', label: 'Tudo' }
    ];

    // Métricas disponíveis baseadas nas leituras cadastradas do sensor
    const allAvailableMetrics = [
      { id: 'temperatura', label: 'Temperatura', color: '#ef4444', reading: 'temperature', unit: '°C' },
      { id: 'umidade', label: 'Umidade', color: '#3b82f6', reading: 'humidity', unit: '%' },
      { id: 'co2', label: 'CO₂', color: '#10b981', reading: 'co2', unit: 'ppm' },
      { id: 'pm25', label: 'PM2.5', color: '#f59e0b', reading: 'pm25', unit: 'µg/m³' },
      { id: 'pm10', label: 'PM10', color: '#f97316', reading: 'pm10', unit: 'µg/m³' },
      { id: 'pm1', label: 'PM1', color: '#fb923c', reading: 'pm1', unit: 'µg/m³' },
      { id: 'voc', label: 'VOC', color: '#8b5cf6', reading: 'voc', unit: 'ppb' },
      { id: 'dewpoint', label: 'Ponto de Orvalho', color: '#06b6d4', reading: 'dewpoint', unit: '°C' },
      { id: 'wetbulb', label: 'Bulbo Úmido', color: '#14b8a6', reading: 'wetbulb', unit: '°C' },
      { id: 'enthalpy', label: 'Entalpia', color: '#ec4899', reading: 'enthalpy', unit: 'kJ/kg' },
      { id: 'absoluteHumidity', label: 'Umidade Absoluta', color: '#6366f1', reading: 'absoluteHumidity', unit: 'g/m³' },
      { id: 'specificHumidity', label: 'Umidade Específica', color: '#8b5cf6', reading: 'specificHumidity', unit: 'g/kg' },
      { id: 'luminosidade', label: 'Luminosidade', color: '#fbbf24', reading: 'luminosity', unit: 'lux' },
      { id: 'ruido', label: 'Ruído', color: '#64748b', reading: 'noise', unit: 'dB' }
    ];
    
    // Filtrar apenas métricas que o sensor possui
    const availableMetrics = useMemo(() => {
      console.log('📊 availableReadings recebido:', availableReadings);
      
      if (!availableReadings || Object.keys(availableReadings).length === 0) {
        // Se não houver informações, mostrar métricas padrão (compatibilidade)
        console.log('⚠️ Sem availableReadings, usando métricas padrão');
        return allAvailableMetrics.slice(0, 6);
      }
      
      const filtered = allAvailableMetrics.filter(metric => {
        const hasReading = availableReadings[metric.reading] === true;
        console.log(`  ${metric.label} (${metric.reading}): ${hasReading}`);
        return hasReading;
      });
      
      console.log(`✅ Métricas filtradas: ${filtered.length} de ${allAvailableMetrics.length}`);
      
      // Se não encontrou nenhuma métrica, retornar métricas padrão
      if (filtered.length === 0) {
        console.log('⚠️ Nenhuma métrica encontrada, usando padrão');
        return allAvailableMetrics.slice(0, 6);
      }
      
      return filtered;
    }, [availableReadings]);
    
    // Inicializar selectedMetrics com as primeiras métricas disponíveis ou do localStorage
    useEffect(() => {
      if (availableMetrics.length === 0 || initializedRef.current) return;
      
      // Carregar do localStorage primeiro
      const storedMetrics = getStoredMetrics();
      const validStoredMetrics = storedMetrics.filter(id => 
        availableMetrics.some(m => m.id === id)
      );
      
      // Se há métricas válidas no localStorage, usar elas (limitadas a 4)
      if (validStoredMetrics.length > 0) {
        const limitedMetrics = validStoredMetrics.slice(0, 4);
        console.log('✅ Carregando métricas do localStorage:', limitedMetrics);
        setLocalSelectedMetrics(limitedMetrics);
        saveMetricsToStorage(limitedMetrics);
      } else if (localSelectedMetrics.length === 0 || 
                 !localSelectedMetrics.some(id => availableMetrics.some(m => m.id === id))) {
        // Se não há métricas selecionadas OU se as métricas selecionadas não estão mais disponíveis
        const initialMetrics = availableMetrics.slice(0, Math.min(3, availableMetrics.length)).map(m => m.id);
        console.log('✅ Inicializando métricas:', initialMetrics);
        setLocalSelectedMetrics(initialMetrics);
        saveMetricsToStorage(initialMetrics);
      }
      
      initializedRef.current = true;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [availableMetrics.length]); // Usar apenas length para evitar loops

    const toggleMetric = (metricId) => {
      setLocalSelectedMetrics(prev => {
        let newMetrics;
        
        if (prev.includes(metricId)) {
          // Remover métrica se já estiver selecionada
          newMetrics = prev.filter(m => m !== metricId);
        } else {
          // Adicionar métrica apenas se não exceder o limite de 4
          if (prev.length >= 4) {
            console.warn('⚠️ Limite de 4 métricas atingido');
            return prev; // Não adicionar se já tiver 4
          }
          newMetrics = [...prev, metricId];
        }
        
        // Salvar no localStorage
        saveMetricsToStorage(newMetrics);
        return newMetrics;
      });
    };
    
    const selectedMetrics = localSelectedMetrics.length > 0 ? localSelectedMetrics : 
                           (availableMetrics.length > 0 ? availableMetrics.slice(0, 3).map(m => m.id) : []);
    
    // Atualizar dados processados quando o período mudar
    const historicalProcessedData = useMemo(() => {
      console.log('📈 Processando dados históricos:', {
        historicalLength: historical?.length || 0,
        selectedPeriod,
        availableReadings
      });
      
      if (!historical || historical.length === 0) {
        console.log('⚠️ Sem dados históricos');
        return [];
      }
      
      // Log do primeiro item para debug
      if (historical.length > 0) {
        console.log('📊 Primeiro item histórico:', historical[0]);
      }
      
      // Processar dados SEM verificar availableReadings primeiro
      // Isso garante que todos os dados disponíveis sejam incluídos
      const processed = historical.map((item) => ({
        timestamp: item.timestamp,
        time: formatTimeForChart(item.timestamp, selectedPeriod),
        // Incluir TODOS os valores disponíveis, independente de availableReadings
        temperatura: item.temperature !== null && item.temperature !== undefined ? item.temperature : null,
        umidade: item.humidity !== null && item.humidity !== undefined ? item.humidity : null,
        co2: item.co2 !== null && item.co2 !== undefined ? item.co2 : null,
        pm25: item.pm25 !== null && item.pm25 !== undefined ? item.pm25 : null,
        pm10: item.pm10 !== null && item.pm10 !== undefined ? item.pm10 : null,
        pm1: item.pm1 !== null && item.pm1 !== undefined ? item.pm1 : null,
        voc: item.voc !== null && item.voc !== undefined ? item.voc : null,
        dewpoint: item.dewpoint !== null && item.dewpoint !== undefined ? item.dewpoint : null,
        wetbulb: item.wetbulb !== null && item.wetbulb !== undefined ? item.wetbulb : null,
        enthalpy: item.enthalpy !== null && item.enthalpy !== undefined ? item.enthalpy : null,
        absoluteHumidity: item.absoluteHumidity !== null && item.absoluteHumidity !== undefined ? item.absoluteHumidity : null,
        specificHumidity: item.specificHumidity !== null && item.specificHumidity !== undefined ? item.specificHumidity : null,
        luminosidade: item.luminosity !== null && item.luminosity !== undefined ? item.luminosity : null,
        ruido: item.noise !== null && item.noise !== undefined ? item.noise : null
      }));
      
      // Contar valores válidos por métrica
      const validCounts = {
        temperatura: processed.filter(d => d.temperatura !== null).length,
        umidade: processed.filter(d => d.umidade !== null).length,
        co2: processed.filter(d => d.co2 !== null).length
      };
      
      console.log('✅ Dados processados:', {
        total: processed.length,
        validCounts,
        sampleData: processed[0]
      });
      
      return processed;
    }, [historical, selectedPeriod]);

    return (
      <div className="historical-container">
        {/* Header Section */}
        <div className="dashboard-header-premium">
          <div className="header-gradient-bg historical"></div>
          <div className="header-content-dash">
            <div className="header-left-dash">
              <motion.div 
                className="header-icon-dash"
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <FiTrendingUp size={36} />
              </motion.div>
    <div>
                <h1 className="header-title-dash">Histórico de Leituras</h1>
                <p className="header-subtitle-dash">Análise temporal das medições do sensor</p>
              </div>
            </div>
            <div className="header-stats">
              <div className="header-stat">
                <span className="stat-label">Período</span>
                <span className="stat-value">{selectedPeriod}</span>
              </div>
              <div className="header-stat">
                <span className="stat-label">Pontos</span>
                <span className="stat-value">{historicalProcessedData.length}</span>
            </div>
              <div className="header-stat">
                <span className="stat-label">Intervalo</span>
                <span className="stat-value">
                  {selectedPeriod === '1h' ? '5 min' : 
                   selectedPeriod === '24h' ? '15 min' : 
                   selectedPeriod === '7d' ? '1h' : 
                   selectedPeriod === '30d' ? '3h' : 
                   selectedPeriod === '6m' ? '6h' : 
                   selectedPeriod === '1y' ? '12h' : 
                   selectedPeriod === 'all' ? '1d' : '15 min'}
                </span>
            </div>
          </div>
        </div>
      </div>

        {/* Controls Bar */}
        <div className="controls-bar">
          <div className="period-selector">
            {periods.map(period => (
          <button
                key={period.id}
                className={`period-btn ${selectedPeriod === period.id ? 'active' : ''}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('🔄 Mudando período para:', period.id);
                  if (onTimeRangeChange) {
                    console.log('📡 Notificando componente pai sobre mudança de período');
                    onTimeRangeChange(period.id);
                  }
                }}
                type="button"
              >
                {period.id === 'all' && <FiCalendar size={14} />}
                {period.label}
          </button>
        ))}
      </div>

          <div className="chart-controls">
            <div className="chart-type-selector">
              <button
                className={`type-btn ${chartType === 'area' ? 'active' : ''}`}
                onClick={() => setChartType('area')}
              >
                <FiBarChart2 size={16} />
              </button>
              <button
                className={`type-btn ${chartType === 'line' ? 'active' : ''}`}
                onClick={() => setChartType('line')}
              >
                <FiTrendingUp size={16} />
              </button>
              <button
                className={`type-btn ${chartType === 'bar' ? 'active' : ''}`}
                onClick={() => setChartType('bar')}
              >
                <FiBarChart2 size={16} style={{ transform: 'rotate(90deg)' }} />
              </button>
            </div>

            <button className="filter-btn">
              <FiFilter size={16} />
              <span>Filtros</span>
            </button>
          </div>
        </div>

        {/* Metrics Selector */}
        <div className="metrics-selector">
          <span className="selector-label">
            Métricas visíveis: 
            <span style={{ fontSize: '12px', color: '#6b7280', marginLeft: '8px', fontWeight: 'normal' }}>
              ({selectedMetrics.length}/4 máximo)
            </span>
          </span>
          <div className="metric-chips">
            {availableMetrics.map(metric => {
              const isSelected = selectedMetrics.includes(metric.id);
              const isDisabled = !isSelected && selectedMetrics.length >= 4;
              
              return (
                <motion.button
                  key={metric.id}
                  className={`metric-chip ${isSelected ? 'active' : ''} ${isDisabled ? 'disabled' : ''}`}
                  onClick={() => !isDisabled && toggleMetric(metric.id)}
                  disabled={isDisabled}
                  whileHover={!isDisabled ? { scale: 1.05 } : {}}
                  whileTap={!isDisabled ? { scale: 0.95 } : {}}
                  style={{
                    borderColor: isSelected ? metric.color : '#e5e7eb',
                    background: isSelected ? `${metric.color}10` : 'white',
                    opacity: isDisabled ? 0.5 : 1,
                    cursor: isDisabled ? 'not-allowed' : 'pointer'
                  }}
                  title={isDisabled ? 'Limite de 4 métricas atingido. Desmarque uma para selecionar outra.' : ''}
                >
                  <div 
                    className="chip-dot"
                    style={{ background: metric.color }}
                  />
                  <span>{metric.label}</span>
                  {isSelected ? 
                    <FiEye size={14} /> : 
                    <FiEyeOff size={14} />
                  }
                </motion.button>
              );
            })}
          </div>
        </div>

        {/* Main Chart */}
        <div className="main-chart-container">
          <ResponsiveContainer width="100%" height={450}>
            {chartType === 'area' && (
              <AreaChart data={historicalProcessedData.filter(d => 
                // Mostrar apenas pontos que têm pelo menos uma métrica selecionada com valor válido
                selectedMetrics.some(m => d[m] !== null && d[m] !== undefined && !isNaN(d[m]))
              )}>
              <defs>
                  {availableMetrics.map(metric => (
                    <linearGradient key={metric.id} id={`gradient-${metric.id}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={metric.color} stopOpacity={0.8}/>
                      <stop offset="95%" stopColor={metric.color} stopOpacity={0.1}/>
                </linearGradient>
                  ))}
              </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis 
                dataKey="time" 
                  stroke="#9ca3af"
                  fontSize={11}
              />
              <YAxis 
                  stroke="#9ca3af"
                  fontSize={11}
              />
              <YAxis 
                  yAxisId="right"
                  orientation="right"
                  stroke="#9ca3af"
                  fontSize={11}
              />
              <Tooltip 
                shared={true}
                contentStyle={{ 
                    background: 'rgba(255, 255, 255, 0.95)',
                    border: 'none',
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    backdropFilter: 'blur(10px)'
                }}
                formatter={(value, name) => {
                  if (value === null || value === undefined || isNaN(value)) {
                    return null; // Não mostrar valores inválidos
                  }
                  // Encontrar a métrica correspondente pelo nome
                  const metric = availableMetrics.find(m => m.label === name);
                  const unit = metric?.unit || getMetricUnit(name.toLowerCase().replace(/\s+/g, ''));
                  return [`${parseFloat(value).toFixed(2)} ${unit}`, name];
                }}
              />
              <Legend />
                {selectedMetrics.map(metricId => {
                  const metric = availableMetrics.find(m => m.id === metricId);
                  if (!metric) return null;
                  
                  // Mapear ID da métrica para dataKey
                  const dataKeyMap = {
                    'temperatura': 'temperatura',
                    'umidade': 'umidade',
                    'co2': 'co2',
                    'pm25': 'pm25',
                    'pm10': 'pm10',
                    'pm1': 'pm1',
                    'voc': 'voc',
                    'dewpoint': 'dewpoint',
                    'wetbulb': 'wetbulb',
                    'enthalpy': 'enthalpy',
                    'absoluteHumidity': 'absoluteHumidity',
                    'specificHumidity': 'specificHumidity',
                    'luminosidade': 'luminosidade',
                    'ruido': 'ruido'
                  };
                  
                  const dataKey = dataKeyMap[metricId];
                  if (!dataKey) return null;
                  
                  // Usar yAxisId="right" apenas para co2
                  const useRightAxis = metricId === 'co2';
                  
                  return (
                    <Area
                      key={metricId}
                      type="monotone"
                      dataKey={dataKey}
                      stroke={metric.color}
                      fill={`url(#gradient-${metricId})`}
                      strokeWidth={2}
                      name={metric.label}
                      yAxisId={useRightAxis ? "right" : undefined}
                      connectNulls={true}
                      dot={(props) => {
                        // Mostrar ponto apenas se o valor for válido
                        const { payload, cx, cy } = props;
                        const value = payload && payload[dataKey];
                        if (value !== null && value !== undefined && !isNaN(value)) {
                          return (
                            <svg x={cx - 3} y={cy - 3} width={6} height={6}>
                              <circle cx={3} cy={3} r={3} fill={metric.color} stroke="#fff" strokeWidth={1} />
                            </svg>
                          );
                        }
                        return null;
                      }}
                      activeDot={{ r: 6, stroke: metric.color, strokeWidth: 2, fill: '#fff' }}
                    />
                  );
                })}
            </AreaChart>
            )}
          </ResponsiveContainer>
        </div>

        {/* Statistics Cards */}
        <div className="statistics-grid">
          {selectedMetrics.map(metricId => {
            const metric = availableMetrics.find(m => m.id === metricId);
            if (!metric) return null; // Pular se métrica não encontrada
            
            // Filtrar valores null/undefined antes de calcular estatísticas
            const allValues = historicalProcessedData.map(d => d[metricId]);
            const values = allValues.filter(v => v !== null && v !== undefined && !isNaN(v));
            
            // Se não houver valores válidos, mostrar card vazio
            if (values.length === 0) {
              return (
                <motion.div 
                  key={metricId}
                  className="stat-card-detailed"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  style={{ borderColor: metric.color, opacity: 0.5 }}
                >
                  <div className="stat-header" style={{ background: `${metric.color}10` }}>
                    <div className="stat-icon" style={{ background: metric.color }}>
                      <FiActivity size={20} />
                    </div>
                    <h4>{metric.label}</h4>
                  </div>
                  <div className="stat-body">
                    <div className="stat-row">
                      <span className="stat-label">Sem dados</span>
                      <span className="stat-value" style={{ color: metric.color }}>
                        N/A
                      </span>
                    </div>
                  </div>
                </motion.div>
              );
            }
            
            // Garantir que temos valores válidos antes de calcular
            // Filtrar novamente para garantir que não há valores inválidos
            const validValues = values.filter(v => v !== null && v !== undefined && !isNaN(v) && isFinite(v));
            
            if (validValues.length === 0) {
              // Se não houver valores válidos, retornar card vazio
              return (
                <motion.div 
                  key={metricId}
                  className="stat-card-detailed"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  style={{ borderColor: metric.color, opacity: 0.5 }}
                >
                  <div className="stat-header" style={{ background: `${metric.color}10` }}>
                    <div className="stat-icon" style={{ background: metric.color }}>
                      <FiActivity size={20} />
                    </div>
                    <h4>{metric.label}</h4>
                  </div>
                  <div className="stat-body">
                    <div className="stat-row">
                      <span className="stat-label">Sem dados</span>
                      <span className="stat-value" style={{ color: metric.color }}>
                        N/A
                      </span>
                    </div>
                  </div>
                </motion.div>
              );
            }
            
            // Calcular estatísticas apenas com valores válidos
            // Função auxiliar para verificar se um valor é válido para toFixed
            const isValidNumber = (val) => {
              return val !== null && 
                     val !== undefined && 
                     !isNaN(val) && 
                     isFinite(val) && 
                     typeof val === 'number';
            };
            
            const min = validValues.length > 0 ? Math.min(...validValues) : null;
            const max = validValues.length > 0 ? Math.max(...validValues) : null;
            const avg = validValues.length > 0 
              ? (validValues.reduce((a, b) => a + b, 0) / validValues.length)
              : null;
            const current = validValues.length > 0 ? validValues[validValues.length - 1] : null;

            return (
              <motion.div 
                key={metricId}
                className="stat-card-detailed"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                whileHover={{ y: -5 }}
                style={{ borderColor: metric.color }}
              >
                <div className="stat-header" style={{ background: `${metric.color}10` }}>
                  <div className="stat-icon" style={{ background: metric.color }}>
                    <FiActivity size={20} />
                  </div>
                  <h4>{metric.label}</h4>
                </div>
                <div className="stat-body">
                  <div className="stat-row">
                    <span className="stat-label">Atual</span>
                    <span className="stat-value" style={{ color: metric.color }}>
                      {isValidNumber(current) ? `${current.toFixed(2)} ${getMetricUnit(metricId)}` : 'N/A'}
                    </span>
                  </div>
                  <div className="stat-row">
                    <span className="stat-label">Média</span>
                    <span className="stat-value">{isValidNumber(avg) ? `${avg.toFixed(2)} ${getMetricUnit(metricId)}` : 'N/A'}</span>
                  </div>
                  <div className="stat-row">
                    <span className="stat-label">Min</span>
                    <span className="stat-value">{isValidNumber(min) ? `${min.toFixed(2)} ${getMetricUnit(metricId)}` : 'N/A'}</span>
                  </div>
                  <div className="stat-row">
                    <span className="stat-label">Max</span>
                    <span className="stat-value">{isValidNumber(max) ? `${max.toFixed(2)} ${getMetricUnit(metricId)}` : 'N/A'}</span>
                  </div>
                </div>
                <div className="stat-footer">
                  <div className="mini-chart">
                    <ResponsiveContainer width="100%" height={40}>
                      <LineChart data={validValues.slice(-10).map((v, i) => ({ v, i }))}>
              <Line 
                type="monotone" 
                          dataKey="v" 
                          stroke={metric.color}
                          strokeWidth={2}
                dot={false}
                connectNulls={true}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
              </motion.div>
            );
          })}
            </div>
          </div>
    );
  };

  // Modal de tela cheia para métrica
  const FullscreenModal = ({ metric, onClose }) => {
    if (!metric) return null;

    return (
      <motion.div 
        className="fullscreen-modal"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <div className="modal-content">
          <div className="modal-header">
            <h2>{metric.name}</h2>
            <button onClick={onClose}>
              <FiMinimize2 size={20} />
            </button>
            </div>
          <div className="modal-body">
            <ResponsiveContainer width="100%" height={400}>
              <AreaChart data={processedData}>
                <defs>
                  <linearGradient id="modalGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={metric.color} stopOpacity={0.8}/>
                    <stop offset="95%" stopColor={metric.color} stopOpacity={0.1}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="time" />
                <YAxis />
                <Tooltip />
                <Area
                  type="monotone"
                  dataKey={metric.id === 'temp' ? 'temperatura' : 
                          metric.id === 'humidity' ? 'umidade' : 
                          metric.id}
                  stroke={metric.color}
                  fill="url(#modalGradient)"
                  connectNulls={true}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </motion.div>
    );
  };

  // Renderização baseada na subseção
  return (
    <div className="dashboard-container">
      <AnimatePresence mode="wait">
        {activeSubsection === 'instant' && (
          <motion.div
            key="instant"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
          >
            <InstantDataView />
          </motion.div>
        )}
        {activeSubsection === 'historical' && (
          <motion.div
            key="historical"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <HistoricalView />
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {fullscreenMetric && (
          <FullscreenModal 
            metric={fullscreenMetric} 
            onClose={() => setFullscreenMetric(null)} 
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default Dashboard;
